import {
  pick,
  assign,
  filter,
  forEach,
  isArray,
  isUndefined,
  has
} from 'min-dash';

import {
  isAny
} from '../modelElements/services/ModelingService';

import {
  is,
  getBusinessObject,
  isExpanded,
  isEventSubProcess
} from '../../base/BaseServices';

import { getPropertyNames } from '../copyPasteElements/ModdleCopy';

function copyProperties(source, target, properties) {
  if (!isArray(properties)) {
    properties = [ properties ];
  }

  forEach(properties, function(property) {
    if (!isUndefined(source[property])) {
      target[property] = source[property];
    }
  });
}

var CUSTOM_PROPERTIES = [
  'cancelActivity',
  'instantiate',
  'eventGatewayType',
  'triggeredByEvent',
  'isInterrupting'
];


function toggeling(element, target) {

  var oldCollapsed = (
    element && has(element, 'collapsed') ? element.collapsed : !isExpanded(element)
  );

  var targetCollapsed;

  if (target && (has(target, 'collapsed') || has(target, 'isExpanded'))) {

    // property is explicitly set so use it
    targetCollapsed = (
      has(target, 'collapsed') ? target.collapsed : !target.isExpanded
    );
  } else {

    // keep old state
    targetCollapsed = oldCollapsed;
  }

  if (oldCollapsed !== targetCollapsed) {
    element.collapsed = oldCollapsed;
    return true;
  }

  return false;
}



/**
 * This module takes care of replacing BPMN elements
 */
export default function ReplaceElements(
  bpmnFactory,
  elementFactory,
  moddleCopy,
  modeling,
  replace,
  rules,
  selection
) {
  /**
   * Prepares a new business object for the replacement element
   * and triggers the replace operation.
   *
   * @param  {djs.model.Base} element
   * @param  {Object} target
   * @param  {Object} [hints]
   *
   * @return {djs.model.Base} the newly created element
   */
  function replaceElement(element, target, hints) {
    hints = hints || {};

    const type = target.type;
    const oldBusinessObject = element.businessObject;

    if (isSubProcess(oldBusinessObject) && type === 'bpmn:SubProcess' && toggeling(element, target)) {
      modeling.toggleCollapse(element);
      return element;
    }

    let newBusinessObject = bpmnFactory.create(type);
    let newElement = {
      type: type,
      businessObject: newBusinessObject
    };
    
    const elementProps = getPropertyNames(oldBusinessObject.$descriptor);
    const newElementProps = getPropertyNames(newBusinessObject.$descriptor, true);
    const copyProps = intersection(elementProps, newElementProps);

    assign(newBusinessObject, pick(target, CUSTOM_PROPERTIES));
    
    const properties = filter(copyProps, (propertyName) => {
      if (propertyName === 'eventDefinitions') {
        return hasEventDefinition(element, target.eventDefinitionType);
      }

      if (propertyName === 'loopCharacteristics') {
        return !isEventSubProcess(newBusinessObject);
      }

      if (has(newBusinessObject, propertyName)) {
        return false;
      }

      if (propertyName === 'processRef' && target.isExpanded === false) {
        return false;
      }

      if (propertyName === 'triggeredByEvent') {
        return false;
      }

      return true;
    });
    
    newBusinessObject = moddleCopy.copyElement(oldBusinessObject, newBusinessObject, properties);

    if (target.eventDefinitionType) {
      if (!hasEventDefinition(newBusinessObject, target.eventDefinitionType)) {
        newElement.eventDefinitionType = target.eventDefinitionType;
        newElement.eventDefinitionAttrs = target.eventDefinitionAttrs;
      }
    }
    
    if (is(oldBusinessObject, 'bpmn:Activity')) {
      if (isSubProcess(oldBusinessObject)) {
        newElement.isExpanded = isExpanded(oldBusinessObject);
      } else if (target && has(target, 'isExpanded')) {
        newElement.isExpanded = target.isExpanded;
      }

      if (isExpanded(oldBusinessObject) && !is(oldBusinessObject, 'bpmn:Task') && newElement.isExpanded) {
        newElement.width = element.width;
        newElement.height = element.height;
      }
    }

    if (is(oldBusinessObject, 'bpmn:Participant')) {
      if (target.isExpanded === true) {
        newBusinessObject.processRef = bpmnFactory.create('bpmn:Process');
      } else {
        hints.moveChildren = false;
      }

      newElement.width = element.width;
      newElement.height = elementFactory._getDefaultSize(newBusinessObject).height;
    }

    if (!rules.allowed('shape.resize', { shape: newBusinessObject })) {
      newElement.height = elementFactory._getDefaultSize(newBusinessObject).height;
      newElement.width = elementFactory._getDefaultSize(newBusinessObject).width;
    }

    newBusinessObject.name = oldBusinessObject.name;

    if (isAny(oldBusinessObject, ['bpmn:ExclusiveGateway', 'bpmn:InclusiveGateway', 'bpmn:Activity']) &&
        isAny(newBusinessObject, ['bpmn:ExclusiveGateway', 'bpmn:InclusiveGateway', 'bpmn:Activity'])) {
      newBusinessObject.default = oldBusinessObject.default;
    }

    if (target.host && !is(oldBusinessObject, 'bpmn:BoundaryEvent') && is(newBusinessObject, 'bpmn:BoundaryEvent')) {
      newElement.host = target.host;
    }

    if (newElement.type === 'bpmn:DataStoreReference' || newElement.type === 'bpmn:DataObjectReference') {
      newElement.x = element.x + (element.width - newElement.width) / 2;
    }
    
    newElement.di = {};
    copyProperties(oldBusinessObject.di, newElement.di, ['fill', 'stroke']);

    const replacedElement = replace.replaceElement(element, newElement, hints);
    
    if (hints.select !== false) {
      selection.select(replacedElement);
    }
    
    return replacedElement;
  }

  this.replaceElement = replaceElement;
}


ReplaceElements.$inject = [
  'bpmnFactory',
  'elementFactory',
  'moddleCopy',
  'modeling',
  'replace',
  'rules',
  'selection'
];


function isSubProcess(bo) {
  return is(bo, 'bpmn:SubProcess');
}

function hasEventDefinition(element, type) {

  var bo = getBusinessObject(element);

  return type && bo.get('eventDefinitions').some(function(definition) {
    return is(definition, type);
  });
}

/**
 * Compute intersection between two arrays.
 */
function intersection(a1, a2) {
  return a1.filter(function(el) {
    return a2.indexOf(el) !== -1;
  });
}
